/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Modal,
  ModalFooter,
  Selection,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getVolumePrefix,
  getNextVolumeNumber,
  getNextCopyNumberForVolume,
  volumeExists,
} from '../utils/volumeUtils';
import BarcodeGenerationModal from '../../ItemsList/modals/BarcodeGenerationModal';
import SequentialBarcodeSection from '../../ItemsList/components/SequentialBarcodeSection';

const DuplicateVolumeModal = ({
  open,
  onClose,
  onDuplicate,
  sourceVolume,
  existingVolumes = [],
  fields,
  // Props for barcode generation
  enableAutoGenerator = false,
  autoGenerate = false,
  generateBarcodeValue,
  locationId,
  locationCode,
  existingBarcodes = [],
}) => {
  const intl = useIntl();
  const [volumePrefix, setVolumePrefix] = useState('مج.');
  const [volumeNumber, setVolumeNumber] = useState('');
  const [customPrefix, setCustomPrefix] = useState('');
  const [isCustomPrefix, setIsCustomPrefix] = useState(false);

  // Barcode generation states
  const [barcodeSettings, setBarcodeSettings] = useState(null);
  const [showAdvancedBarcodeModal, setShowAdvancedBarcodeModal] =
    useState(false);

  // Predefined prefixes
  const prefixOptions = [
    { value: 'مج.', label: 'مج.' },
    { value: 'VOL.', label: 'VOL.' },
    { value: 'custom', label: 'Custom' },
  ];

  // Initialize with next expected volume number
  useEffect(() => {
    if (open) {
      const nextNumber = getNextVolumeNumber(existingVolumes);
      setVolumeNumber(nextNumber.toString());

      // Try to detect source volume prefix
      const sourcePrefix = getVolumePrefix(sourceVolume);
      if (
        sourcePrefix &&
        prefixOptions.some((option) => option.value === sourcePrefix)
      ) {
        setVolumePrefix(sourcePrefix);
        setIsCustomPrefix(false);
      } else if (sourcePrefix) {
        setCustomPrefix(sourcePrefix);
        setVolumePrefix(sourcePrefix);
        setIsCustomPrefix(true);
      }
    }
  }, [open, existingVolumes, sourceVolume]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setVolumePrefix('مج.');
      setVolumeNumber('');
      setCustomPrefix('');
      setIsCustomPrefix(false);
      setBarcodeSettings(null);
    }
  }, [open]);

  const handlePrefixChange = (value) => {
    if (value === 'custom') {
      setIsCustomPrefix(true);
      setVolumePrefix('');
    } else {
      setIsCustomPrefix(false);
      setVolumePrefix(value);
      setCustomPrefix('');
    }
  };

  const handleCustomPrefixChange = (e) => {
    const value = e.target.value;
    setCustomPrefix(value);
    setVolumePrefix(value);
  };

  // Handle barcode settings change from SequentialBarcodeSection
  const handleBarcodeSettingsChange = (settings) => {
    setBarcodeSettings(settings);
  };

  const getFullVolumeString = () => {
    const prefix = isCustomPrefix ? customPrefix : volumePrefix;
    return volumeNumber ? `${prefix}${volumeNumber}` : '';
  };

  const handleDuplicate = () => {
    const fullVolumeString = getFullVolumeString();
    if (fullVolumeString) {
      onDuplicate(fullVolumeString, barcodeSettings);
      onClose();
    }
  };

  const handleAdvancedBarcodeConfirm = (settings) => {
    setShowAdvancedBarcodeModal(false);
    onDuplicate(getFullVolumeString(), settings);
    onClose();
  };

  const isVolumeExists = volumeExists(getFullVolumeString(), existingVolumes);
  const isValidVolumeNumber =
    volumeNumber &&
    !Number.isNaN(parseInt(volumeNumber, 10)) &&
    parseInt(volumeNumber, 10) > 0;
  const isValidPrefix = isCustomPrefix
    ? customPrefix.trim() !== ''
    : volumePrefix !== '';

  const sourceItemsCount =
    fields?.value?.filter(
      (item) => (item.volume || 'No_Volume') === sourceVolume,
    ).length || 0;

  return (
    <Modal
      open={open}
      label={
        <FormattedMessage
          id="ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.label"
          defaultMessage="Duplicate Volume"
        />
      }
      onClose={onClose}
      size="small"
    >
      <div style={{ padding: '20px' }}>
        <p style={{ marginBottom: '16px' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.description"
            values={{
              sourceVolume,
              itemCount: sourceItemsCount,
            }}
            defaultMessage="Duplicate volume {sourceVolume} ({itemCount} items) to a new volume:"
          />
        </p>

        {/* Volume Prefix Selection */}
        <div style={{ marginBottom: '16px' }}>
          <Selection
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.volumePrefix.label"
                defaultMessage="Volume Prefix"
              />
            }
            placeholder={intl.formatMessage({
              id: 'ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.volumePrefix.placeholder',
              defaultMessage: 'Select prefix',
            })}
            dataOptions={prefixOptions}
            value={isCustomPrefix ? 'custom' : volumePrefix}
            onChange={handlePrefixChange}
          />
        </div>

        {/* Custom Prefix Input */}
        {isCustomPrefix && (
          <div style={{ marginBottom: '16px' }}>
            <TextField
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.customPrefix.label"
                  defaultMessage="Custom Prefix"
                />
              }
              value={customPrefix}
              onChange={handleCustomPrefixChange}
              placeholder={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.customPrefix.placeholder',
                defaultMessage: 'Enter custom prefix',
              })}
              required
            />
          </div>
        )}

        {/* Volume Number Input */}
        <div style={{ marginBottom: '16px' }}>
          <TextField
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.volumeNumber.label"
                defaultMessage="New Volume Number"
              />
            }
            type="number"
            min="1"
            value={volumeNumber}
            onChange={(e) => setVolumeNumber(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.volumeNumber.placeholder',
              defaultMessage: 'Enter volume number',
            })}
            error={
              isVolumeExists ? (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.volumeExists.error"
                  defaultMessage="This volume already exists"
                />
              ) : (
                ''
              )
            }
            required
          />
        </div>

        {/* Volume Preview */}
        {getFullVolumeString() && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                color: '#6c757d',
                marginBottom: '4px',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.preview.label"
                defaultMessage="New Volume Preview:"
              />
            </div>
            <div
              style={{ fontSize: '16px', fontWeight: '600', color: '#495057' }}
            >
              {getFullVolumeString()}
            </div>
          </div>
        )}

        {/* Copy Number Info */}
        {getFullVolumeString() && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#e8f5e8',
              border: '1px solid #c3e6c3',
              borderRadius: '4px',
            }}
          >
            <div style={{ fontSize: '14px', color: '#2d5a2d' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.copyNumberInfo"
                values={{
                  startNumber: getNextCopyNumberForVolume(
                    fields,
                    getFullVolumeString(),
                  ),
                  endNumber:
                    getNextCopyNumberForVolume(fields, getFullVolumeString()) +
                    sourceItemsCount -
                    1,
                  itemCount: sourceItemsCount,
                }}
                defaultMessage="Copy numbers will start from {startNumber} to {endNumber} ({itemCount} items)"
              />
            </div>
          </div>
        )}

        {/* Sequential Barcode Section */}
        {sourceItemsCount >= 1 && (
          <SequentialBarcodeSection
            itemCount={sourceItemsCount}
            onBarcodeSettingsChange={handleBarcodeSettingsChange}
            existingBarcodes={existingBarcodes}
            sectionTitle={intl.formatMessage(
              {
                id: 'ui-plugin-inventory-templates.sequentialBarcode.section.title',
                defaultMessage: 'Barcode Generation ({count} items)',
              },
              { count: sourceItemsCount },
            )}
            checkboxLabel={intl.formatMessage({
              id: 'ui-plugin-inventory-templates.sequentialBarcode.useSequential.label',
              defaultMessage: 'Generate sequential barcodes',
            })}
            placeholder={intl.formatMessage({
              id: 'ui-plugin-inventory-templates.sequentialBarcode.startingBarcode.placeholder',
              defaultMessage: 'e.g., BOOK2024001',
            })}
            onAdvancedClick={() => setShowAdvancedBarcodeModal(true)}
          />
        )}
      </div>

      {/* Advanced Barcode Generation Modal */}
      {showAdvancedBarcodeModal && (
        <BarcodeGenerationModal
          open={showAdvancedBarcodeModal}
          onClose={() => setShowAdvancedBarcodeModal(false)}
          onConfirm={handleAdvancedBarcodeConfirm}
          itemCount={sourceItemsCount}
          existingBarcodes={existingBarcodes}
          modalTitle={intl.formatMessage({
            id: 'ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.advancedBarcodeModal.title',
            defaultMessage: 'Advanced Barcode Generation Settings',
          })}
        />
      )}

      <ModalFooter>
        <Button
          buttonStyle="primary"
          onClick={handleDuplicate}
          disabled={
            !isValidVolumeNumber ||
            !isValidPrefix ||
            isVolumeExists ||
            (barcodeSettings && !barcodeSettings.isValid)
          }
          marginBottom0
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.volumeManagement.duplicateVolumeModal.duplicate"
            defaultMessage="Duplicate"
          />
        </Button>
        <Button onClick={onClose} marginBottom0>
          <FormattedMessage
            id="ui-plugin-inventory-templates.volumeManagement.modal.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

DuplicateVolumeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  sourceVolume: PropTypes.string,
  existingVolumes: PropTypes.arrayOf(PropTypes.string),
  fields: PropTypes.object,
  // Barcode generation props
  existingBarcodes: PropTypes.arrayOf(PropTypes.string),
};

export default DuplicateVolumeModal;
